import { useCallback, useState } from "react";
import { usePathname } from "next/navigation";
import { getShareById } from "@/actions/db/share-cover";
import { getNormalizedTitle } from "@/utils/history-helper";
import { FeatureType } from "@/app/(home)/shared/[featureType]/[id]/page";

export type SharedModelData = {
  modelName: string;
  thumbnailUrl: string | null;
};

export type CoverType = "Combined" | "Vocal" | "Instrumental";

export interface SharedCoverData {
  id: string;
  featureType: FeatureType;
  coverLink: string;
  coverFileName: string;
  metadata?: SharedModelData | null;
}

export const useSharedCover = (initialData?: SharedCoverData | null) => {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(true);
  const [sharedCoverData, setSharedCoverData] =
    useState<SharedCoverData | null>(initialData || null);

  const extractPathParams = useCallback(() => {
    const parts = pathname.split("/");
    return {
      featureType: parts[2] as FeatureType,
      shareId: parts[3],
    };
  }, [pathname]);

  const loadSharedCoverData = useCallback(async () => {
    try {
      const { featureType, shareId } = extractPathParams();
      if (!featureType || !shareId) return;

      const shareInfo = await getShareById(shareId);
      if (!shareInfo) return;

      const transformedData: SharedCoverData = {
        id: shareInfo.id,
        featureType: shareInfo.featureType as FeatureType,
        coverLink: shareInfo.downloadUrl,
        coverFileName: decodeURIComponent(
          getNormalizedTitle(shareInfo.downloadUrl),
        ),
        metadata: sharedCoverData?.metadata,
      };
      console.log(sharedCoverData);

      setSharedCoverData(transformedData);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [extractPathParams]);

  const shareContent = async (
    featureType: FeatureType,
    contentId: string,
    downloadUrl: string,
  ): Promise<string> => {
    try {
      const encodedUrl = encodeURIComponent(decodeURIComponent(downloadUrl));
      const response = await fetch(
        `/api/share?featureType=${featureType}&contentId=${contentId}&url=${encodedUrl}`,
        { method: "POST" },
      );

      if (!response.ok) return "";

      const { shared } = await response.json();
      return createSharedUrl(shared.id, featureType);
    } catch (error) {
      console.error("Sharing failed:", error);
      return "";
    }
  };

  const createSharedUrl = (shareId: string, featureType: FeatureType) => {
    return `${window.location.origin}/shared/${featureType}/${shareId}`;
  };

  const tryCreateShareByDownloadUrl = async (
    url: string,
    featureType: FeatureType,
    contentId: string,
  ) => {
    try {
      const encodedUrl = encodeURIComponent(url);
      const response = await fetch(
        `/api/share?featureType=${featureType}&contentId=${contentId}&url=${encodedUrl}`,
        { method: "POST" },
      );

      if (!response.ok) throw new Error("Share creation failed");

      const { shared } = await response.json();
      return createSharedUrl(shared.id, featureType);
    } catch (error) {
      console.error("Sharing failed:", error);
      throw error;
    }
  };

  const getShareByUrl = async (
    url: string,
  ): Promise<SharedCoverData | null> => {
    try {
      const response = await fetch(`/api/share?url=${encodeURIComponent(url)}`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error("Failed to fetch share:", error);
      return null;
    }
  };

  return {
    isLoading,
    sharedCoverData,
    shareContent,
    loadSharedCoverData,
    createSharedUrl,
    tryCreateShareByDownloadUrl,
    getShareByUrl,
  };
};
